import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  message
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { addTask, updateTask } from '@/api/tasks';
import { useAuth } from '@/contexts/AuthContext';
import { AmenitySelector } from '@/components/common/AmenitySelector';
import { BlockApartmentUnitSelector } from '@/components/common/BlockApartmentUnitSelector';
import { AssetSelector } from '@/components/common/AssetSelector';
import type { Task, TaskStatus, TaskPriority, TaskType } from '@/types/employee/task';
import { formatFirestoreTimestamp } from '@/utils/date';
import dayjs from 'dayjs';
import AssignedEmployeeSelector from '@/components/common/AssignedEmployeeSelector';
import { removeUndefined } from '@/utils/removeUndefined';
import { getEmployees } from '@/api/employees';

const { Option } = Select;
const { TextArea } = Input;

const TASK_STATUSES: { value: TaskStatus; label: string }[] = [
  { value: 'pending', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }
];

const TASK_PRIORITIES: { value: TaskPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' }
];

const TASK_TYPES: { value: TaskType; label: string }[] = [
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'security', label: 'Security' },
  { value: 'other', label: 'Other' }
];

const LOCATION_TYPES = [
  { value: 'none', label: 'None' },
  { value: 'amenity', label: 'Amenity' },
  { value: 'apartmentUnit', label: 'Apartment Unit' }
];

interface TaskModalProps {
  open: boolean;
  task?: Task | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const TaskModal: React.FC<TaskModalProps> = ({
  open,
  task,
  onSuccess,
  onCancel
}) => {
  const { t } = useTranslation(['tasks', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Task>();

  const [amenityData, setAmenityData] = useState<{ id: string; name: string } | null>(null);
  const [locationType, setLocationType] = useState<'none' | 'amenity' | 'apartmentUnit'>('amenity');
  const [apartmentUnitData, setApartmentUnitData] = useState<{ id: string; name: string } | null>(null);
  const [assetData, setAssetData] = useState<{ id: string; name: string } | null>(null);

  // Fetch all employees for the workspace (for assignedTo/assignedBy lookup)
  const { data: employeesData } = useQuery({
    queryKey: ['employees', firestoreUser?.workspace?.id],
    queryFn: () => getEmployees({ firestoreUser: firestoreUser!, pageSize: 100 }),
    enabled: !!firestoreUser?.workspace?.id,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (open && task) {
      let locType: 'none' | 'amenity' | 'apartmentUnit' = 'none';
      if (task.amenity) locType = 'amenity';
      else if (task.apartmentUnit) locType = 'apartmentUnit';
      setLocationType(locType);
      form.setFieldsValue({
        ...task,
        dueDate: task.dueDate ? dayjs(formatFirestoreTimestamp(task.dueDate)) : undefined,
      });
      setAmenityData(task.amenity || null);
      setApartmentUnitData(task.apartmentUnit || null);
      setAssetData(task.asset || null);
    } else if (open && !task) {
      form.resetFields();
      form.setFieldsValue({
        status: 'pending',
        priority: 'medium',
        type: 'maintenance',
        locationType: 'none',
      } as any);
      setAmenityData(null);
      setApartmentUnitData(null);
      setAssetData(null);
      setLocationType('none');
    }
  }, [open, task, form]);

  const addTaskMutation = useMutation({
    mutationFn: (data: any) => addTask(data, firestoreUser!),
    onSuccess: () => {
      message.success(t('tasks:createSuccess'));
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      onSuccess();
    },
    onError: (error: any) => {
      message.error(error.message || t('common:errorOccurred'));
    }
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => updateTask(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('tasks:updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      onSuccess();
    },
    onError: (error: any) => {
      message.error(error.message || t('common:errorOccurred'));
    }
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      // Convert employee IDs to required { id, name } objects
      const assignedToId = typeof values.assignedTo === 'string' ? values.assignedTo : values.assignedTo?.id;
      const assignedById = typeof values.assignedBy === 'string' ? values.assignedBy : values.assignedBy?.id;
      const assignedToEmployee = employeesData?.employees.find(e => e.id === assignedToId);
      const assignedByEmployee = employeesData?.employees.find(e => e.id === assignedById);
      
      if (!assignedToEmployee || !assignedByEmployee) {
        message.error(t('tasks:assignedToRequired'));
        return;
      }
      
      const basePayload: any = {
        ...values,
        assignedTo: { id: assignedToEmployee.id, name: assignedToEmployee.user.displayName },
        assignedBy: { id: assignedByEmployee.id, name: assignedByEmployee.user.displayName },
        dueDate: values.dueDate && typeof (values.dueDate as any).toDate === 'function' ? (values.dueDate as any).toDate() : values.dueDate,
        amenity: locationType === 'amenity' ? amenityData || undefined : undefined,
        apartmentUnit: locationType === 'apartmentUnit' ? apartmentUnitData || undefined : undefined,
        asset: assetData || undefined
      };
      // Remove UI-only field if present
      if ('locationType' in basePayload) {
        delete basePayload.locationType;
      }
      // Remove completedAt if not present
      if (values.completedAt === undefined || values.completedAt === null) {
        delete basePayload.completedAt;
      }
      const payload = removeUndefined(basePayload);
      if (task) {
        await updateTaskMutation.mutateAsync({ id: task.id, data: payload });
      } else {
        await addTaskMutation.mutateAsync(payload);
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setAmenityData(null);
    setApartmentUnitData(null);
    setAssetData(null);
    onCancel();
  };

  const isLoading = addTaskMutation.isPending || updateTaskMutation.isPending;

  return (
    <Modal
      title={task ? t('tasks:editTask') : t('tasks:addTask')}
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText={task ? t('common:actions.save') : t('common:actions.add')}
      cancelText={t('common:actions.cancel')}
      confirmLoading={isLoading}
      width={800}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: 'pending' as TaskStatus,
          priority: 'medium' as TaskPriority,
          type: 'maintenance' as TaskType,
          locationType: 'none',
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="title"
              label={t('tasks:title')}
              rules={[{ required: true, message: t('tasks:titleRequired') }]}
            >
              <Input placeholder={t('tasks:taskTitlePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="type"
              label={t('tasks:typeLabel')}
              rules={[{ required: true, message: t('tasks:typeRequired') }]}
            >
              <Select placeholder={t('tasks:selectType')}>
                {TASK_TYPES.map(type => (
                  <Option key={type.value} value={type.value}>{type.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label={t('tasks:description')}
          rules={[{ required: true, message: t('tasks:descriptionRequired') }]}
        >
          <TextArea rows={4} placeholder={t('tasks:taskDescPlaceholder')} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="status"
              label={t('tasks:statusLabel')}
              rules={[{ required: true, message: t('tasks:statusRequired') }]}
            >
              <Select placeholder={t('tasks:selectStatus')}>
                {TASK_STATUSES.map(status => (
                  <Option key={status.value} value={status.value}>{status.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="priority"
              label={t('tasks:priorityLabel')}
              rules={[{ required: true, message: t('tasks:priorityRequired') }]}
            >
              <Select placeholder={t('tasks:selectPriority')}>
                {TASK_PRIORITIES.map(priority => (
                  <Option key={priority.value} value={priority.value}>{priority.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="dueDate"
              label={t('tasks:dueDate')}
              rules={[{ required: true, message: t('tasks:dueDateRequired') }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="locationType"
          label={t('tasks:locationType', 'Location Type')}
        >
          <Select
            onChange={val => setLocationType(val)}
            disabled={isLoading}
          >
            {LOCATION_TYPES.map(opt => (
              <Option key={opt.value} value={opt.value}>{opt.label}</Option>
            ))}
          </Select>
        </Form.Item>

        {locationType === 'amenity' && (
          <AmenitySelector
            disabled={isLoading}
            onAmenityDataChange={setAmenityData}
          />
        )}
        {locationType === 'apartmentUnit' && (
          <BlockApartmentUnitSelector
            disabled={isLoading}
            onApartmentUnitDataChange={setApartmentUnitData}
          />
        )}

        <AssetSelector
          disabled={isLoading}
          onAssetDataChange={setAssetData}
        />

        <AssignedEmployeeSelector disabled={isLoading} />
      </Form>
    </Modal>
  );
};

export default TaskModal; 